import { GlobalMessages } from '@shared/localization/messages';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import { useMemo, useRef } from 'react';

import { useEstimationModelQueryActions } from '@/api/hooks/estimationModelQueryActions';
import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import type {
  EstimationAdditionalFieldDefinitionResponse,
  EstimationAdditionalFieldResponse,
  EstimationModelResponse,
  ProjectWithEstimationResponse,
  UpsertEstimationAdditionalFieldRequest,
} from '@/api/schema/data-contracts';
import { ApiEstimationAdditionalFieldType, ApiEstimationStage } from '@/api/schema/data-contracts';
import { Loader } from '@/components/atoms/Loader';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';
import { sortExpressions } from '@/utils/sorting';

import { Layout } from '../../Layout';
import { MetadataForm } from './MetadataForm';
import type { AdditionalField, AdditionalFieldsFormModel } from './model';

const EstimationDetailMetadataTab = () => {
  const { estimationId } = Route.useLoaderData();

  // Ref-based guard to prevent concurrent mutations (synchronous, not subject to React batching)
  const isSavingRef = useRef(false);
  const toast = useToast();
  const globalMessages = useMessages(GlobalMessages);

  const { useGetEstimation } = useEstimationQueryActions();
  const { data: projectWithEstimation, isLoading: isLoadingEstimation } = useGetEstimation(
    estimationId,
  ) as { data: ProjectWithEstimationResponse | undefined; isLoading: boolean };

  const {
    useGetEstimationAdditionalFields,
    useUpdateEstimationAdditionalFields,
    useUpdateEstimation,
  } = useEstimationQueryActions();
  const { data: additionalFields, isLoading: isLoadingAdditionalFields } =
    useGetEstimationAdditionalFields(estimationId) as {
      data: EstimationAdditionalFieldResponse[] | undefined;
      isLoading: boolean;
    };
  const additionalFieldsUpdateMutation = useUpdateEstimationAdditionalFields();
  const { mutateAsync: updateEstimation } = useUpdateEstimation();

  const { useGetEstimationModel } = useEstimationModelQueryActions();
  const { data: estimationModel, isLoading: isLoadingEstimationModel } = useGetEstimationModel(
    projectWithEstimation?.estimation.estimationModelId,
    projectWithEstimation?.estimation.cultureName,
  ) as { data: EstimationModelResponse | undefined; isLoading: boolean };

  const hasEditPermission = useHasEstimationPermission(estimationId, 'CAN_EDIT');

  const additionalFieldsValues = useMemo<AdditionalField[] | undefined>(() => {
    if (!estimationModel || !additionalFields) {
      return undefined;
    }
    return estimationModel.additionalFields
      .sort(sortExpressions.byOrder)
      .map((fieldDefinition: EstimationAdditionalFieldDefinitionResponse) => {
        const value =
          additionalFields.find(
            (field: EstimationAdditionalFieldResponse) =>
              field.estimationModelAdditionalFieldDefinitionId === fieldDefinition.id,
          )?.value ??
          (fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.TEXT ||
          fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.FORMATTED_TEXT
            ? ''
            : null);

        return {
          estimationModelAdditionalFieldDefinitionId: fieldDefinition.id,
          fieldType: fieldDefinition.additionalFieldType,
          value:
            fieldDefinition.additionalFieldType === ApiEstimationAdditionalFieldType.DATE &&
            value !== null
              ? dayjs(value).toDate()
              : value,
        };
      });
  }, [estimationModel, additionalFields]);

  if (isLoadingEstimation || isLoadingAdditionalFields || isLoadingEstimationModel) {
    return (
      <Layout>
        <Loader />
      </Layout>
    );
  }

  if (!projectWithEstimation || !additionalFields || !estimationModel) {
    return (
      <Layout>
        <Loader />
      </Layout>
    );
  }

  const onSubmit = async (formModel: AdditionalFieldsFormModel): Promise<void> => {
    if (isSavingRef.current) return;

    const upsertEstimation: UpsertEstimationAdditionalFieldRequest[] = formModel.fields.map(
      (field) => ({
        id: additionalFields.find(
          (x: EstimationAdditionalFieldResponse) =>
            x.estimationModelAdditionalFieldDefinitionId ===
            field.estimationModelAdditionalFieldDefinitionId,
        )?.id,
        estimationModelAdditionalFieldDefinitionId:
          field.estimationModelAdditionalFieldDefinitionId,
        value:
          field.fieldType === ApiEstimationAdditionalFieldType.DATE && field.value !== null
            ? dayjs(field.value).format('YYYY-MM-DD')
            : (field.value as string | null),
      }),
    );
    const isChanged = !isEqual(
      sortBy(additionalFields, (x) => x.estimationModelAdditionalFieldDefinitionId).map((x) =>
        pick(x, ['estimationModelAdditionalFieldDefinitionId', 'value']),
      ),
      sortBy(formModel.fields, (x) => x.estimationModelAdditionalFieldDefinitionId),
    );
    if (isChanged) {
      isSavingRef.current = true;
      // Wrap mutate in a Promise so callers can await completion
      return new Promise((resolve) => {
        additionalFieldsUpdateMutation.mutate(
          { estimationId, upsertEstimationAdditionalFields: upsertEstimation },
          {
            onSuccess: () => {
              toast.showToast(globalMessages.savedSuccessfully(), 'success', {
                toastId: 'metadata-autosave',
                autoClose: 2000,
              });
            },
            onError: () => {
              toast.showToast(globalMessages.unexpectedError(), 'error');
            },
            onSettled: () => {
              isSavingRef.current = false;
              resolve();
            },
          },
        );
      });
    }
  };

  const onProjectDescriptionSubmit = async (projectDescription: string) => {
    if (isSavingRef.current) return;

    if (projectDescription !== (projectWithEstimation.estimation.projectDescription ?? '')) {
      isSavingRef.current = true;
      try {
        await updateEstimation({
          estimationId,
          updateProjectWithEstimation: {
            estimation: {
              ...projectWithEstimation.estimation,
              projectDescription: projectDescription || undefined,
            },
            project: {
              name: projectWithEstimation.project.name,
            },
          },
        });
        toast.showToast(globalMessages.savedSuccessfully(), 'success', {
          toastId: 'metadata-autosave',
          autoClose: 2000,
        });
      } catch {
        toast.showToast(globalMessages.unexpectedError(), 'error');
      } finally {
        isSavingRef.current = false;
      }
    }
  };

  const isReadOnly =
    projectWithEstimation.estimation.stage === ApiEstimationStage.IN_REVIEW ||
    projectWithEstimation.estimation.stage === ApiEstimationStage.APPROVED ||
    !hasEditPermission;

  return (
    <Layout>
      {additionalFieldsValues && (
        <MetadataForm
          estimationModel={estimationModel}
          additionalFields={additionalFieldsValues}
          projectWithEstimation={projectWithEstimation}
          isReadOnly={isReadOnly}
          onSubmit={onSubmit}
          onProjectDescriptionSubmit={onProjectDescriptionSubmit}
        />
      )}
    </Layout>
  );
};

export default EstimationDetailMetadataTab;
